import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { debounceTime, finalize, map, startWith, switchMap } from 'rxjs/operators';
import { TagsShowMoreComponent } from 'src/app/pages/buyer/company-search/modals/tags-show-more/tags-show-more.component';
import { CompanyPageRequestDto } from 'src/portal/search-api/models/company-page-request-dto';
import { FacetDto } from 'src/portal/search-api/models/facet-dto';
import { GlobalSearchResultTypeEnum } from 'src/portal/search-api/models/global-search-result-type-enum';
import { CompanyService } from 'src/portal/search-api/services/company.service';
import { SuggestService } from 'src/portal/search-api/services/suggest.service';

@Component({
  selector: 'search-filter-autocomplete-include-exclude',
  templateUrl: './search-filter-autocomplete-include-exclude.component.html',
  styleUrls: ['./search-filter-autocomplete-include-exclude.component.scss']
})
export class SearchFilterAutocompleteIncludeExcludeComponent implements OnInit {

  @Input() label: string;
  @Input() placeholder: string;
  @Input() includeItems: string[] = [];
  @Input() excludeItems: string[] = [];
  @Input() searchField: string;
  @Input() iconName: string;
  @Input() expanded: boolean = false;
  @Input() searhType: any = GlobalSearchResultTypeEnum.COMPANY;

  @Output() includeItemsChange = new EventEmitter<string[]>();
  @Output() excludeItemsChange = new EventEmitter<string[]>();

  @ViewChild('auto') auto: ElementRef;
  @ViewChild('includeInput') includeInput: ElementRef;
  @ViewChild('excludeInput') excludeInput: ElementRef;

  addOnBlur = true;

  loading: boolean;
  includeInputControl = new FormControl();
  excludeInputControl = new FormControl();
  separatorKeysCodes: number[] = [ENTER, COMMA];
  includeFilteredItems: Observable<any[]>;
  excludeFilteredItems: Observable<any[]>;

  constructor(
    private suggestService: SuggestService,
    private companyService: CompanyService,
    public dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.setupFilteredItems();
  }

  private setupFilteredItems() {
    this.includeFilteredItems = this.includeInputControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap((value) => {
        return this._filter(value);
      }),
    );

    this.excludeFilteredItems = this.excludeInputControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap((value) => {
        return this._filter(value);
      }),
    );
  }

  remove(item: string, type: 'include' | 'exclude'): void {
    if (type === 'include') {
      const index = this.includeItems.indexOf(item);
      if (index >= 0) {
        this.includeItems.splice(index, 1);
        this.includeItemsChange.emit(this.includeItems);
        this.setupFilteredItems();
      }
    } else {
      const index = this.excludeItems.indexOf(item);
      if (index >= 0) {
        this.excludeItems.splice(index, 1);
        this.excludeItemsChange.emit(this.excludeItems);
        this.setupFilteredItems();
      }
    }
  }

  selected(event: MatAutocompleteSelectedEvent, type: 'include' | 'exclude'): void {
    if (!event.option) {
      return;
    }
    let value = event.option.value.name;

    if ((value || '').trim()) {
      if (type === 'include') {
        this.includeItems.push(event.option.viewValue);
        this.includeItemsChange.emit(this.includeItems);
        this.includeInputControl.setValue('');
        this.includeInput.nativeElement.value = '';

      } else {
        this.excludeItems.push(event.option.viewValue);
        this.excludeItemsChange.emit(this.excludeItems);
        this.excludeInputControl.setValue('');
        this.excludeInput.nativeElement.value = '';
      }
    }
  }

  private _filter(value: string): Observable<FacetDto[]> {
    this.loading = true;
    const filterValue = value?.toLowerCase();
    if (this.searchField != 'companyName') {
      return this.suggestSearch(filterValue);
    } else {
      return this.companySearch(filterValue);
    }
  }

  suggestSearch(filterValue): Observable<FacetDto[]> {
    return this.suggestService
      .suggest({
        entity: this.searhType,
        field: this.searchField,
        search: filterValue,
      })
      .pipe(
        map((result: FacetDto[]) => {
          const filteredResult = result?.filter((item: FacetDto) => {
            return !this.includeItems.some((otherItem: string) => {
              return item.name.toLowerCase() === otherItem.toLowerCase();
            }) && !this.excludeItems.some((otherItem: string) => {
              return item.name.toLowerCase() === otherItem.toLowerCase();
            });
          });
          return filteredResult;
        }),
        finalize(() => (this.loading = false))
      );
  }

  companySearch(filterValue): Observable<any[]> {
    let body: CompanyPageRequestDto = {
      pageNumber: 0,
      pageSize: 10,
      filters: {
        search: filterValue,
        filterType: GlobalSearchResultTypeEnum.COMPANY,
      },
      sort: [],
    };
    return this.companyService.findCompanies({ body: body }).pipe(
      map((res) => res.content?.map((x: any) => ({
        id: x.id,
        name: x.companyName,
        website: x.website
      })) ),
      map((res: FacetDto[]) => res?.filter(item =>
        !this.includeItems.some(selectedItem => selectedItem?.toLowerCase() === item.name?.toLowerCase()) &&
        !this.excludeItems.some(selectedItem => selectedItem?.toLowerCase() === item.name?.toLowerCase())
      )),
      finalize(() => (this.loading = false))
    );
  }

  tagsShowMore(items: string[], type: 'include' | 'exclude') {
    let data = {};
    if (type === 'include') {
      data = { selectedIncludeItems: items };
    } else {
      data = { selectedExcludeItems: items };
    }

    const dialogRef = this.dialog.open(TagsShowMoreComponent, {
      width: '500px',
      data: data,
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        if (type === 'include') {
          this.includeItems = data.selectedIncludeItems
          this.includeItemsChange.emit(this.includeItems);
        } else {
          this.excludeItems = data.selectedExcludeItems;
          this.excludeItemsChange.emit(this.excludeItems);
        }
        this.setupFilteredItems();
      }
    });
  }
}
